import { atom, useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useMarketConfig } from '~/app/market-config';
import { CMS_DEFAULT_STOCKCAR_RANGE_MIN, CMS_DEFAULT_STOCKCAR_RANGE_STEP } from '~/app/models/constans';
import CarMatchingResultsDialog from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/core/car-matching-results-dialog';
import UserLocationView from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/core/location-input';
import { GeoData, StockCarView } from '~/app/pages/configurator/components/dialogs/stock-car-dialog/models/types';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { Dialog } from '~/components/dialog';

const stockCarDialogOpen = atom(false);
const stockCarView = atom<StockCarView>('UserLocationView');
const locationState = atom('');
const radiusState = atom(0);
const geoState = atom<GeoData>({ lat: 0, lng: 0 });

export const useStockCarDialog = (geoFilter: boolean) => {
	const [open, setLocationDialogOpen] = useAtom(stockCarDialogOpen);
	const [locationView, setLocationView] = useAtom(stockCarView);

	return {
		showStockCarDialog: useCallback(() => {
			setLocationDialogOpen(true);
			setLocationView(geoFilter ? 'UserLocationView' : 'StockCarView');
		}, [setLocationDialogOpen, setLocationView, geoFilter]),
		locationView,
		setLocationView,
		open,
		close: useCallback(() => {
			setLocationDialogOpen(false);
			appTrackingService.summaryClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: `configurator - click to close layer`,
				componentName: 'configurator-stock-car-matching',
				elementName: 'icon',
			});
			appTrackingService.viewChangeEvent({
				eventName: 'generic',
			});
		}, [setLocationDialogOpen]),
	};
};

export const StockCarDialog = () => {
	const { config } = useMarketConfig();
	const { open, close, locationView } = useStockCarDialog(config.stockCarConfig.geoFilter);
	const [geoData, setGeoData] = useAtom<GeoData>(geoState);
	const [radius, setRadius] = useAtom(radiusState);
	const [location, setLocation] = useAtom(locationState);

	if (radius === 0)
		setRadius(
			(config.stockCarConfig.rangeFilter?.step || CMS_DEFAULT_STOCKCAR_RANGE_STEP) <
				(config.stockCarConfig.rangeFilter?.min || CMS_DEFAULT_STOCKCAR_RANGE_MIN)
				? config.stockCarConfig.rangeFilter?.min || CMS_DEFAULT_STOCKCAR_RANGE_MIN
				: config.stockCarConfig.rangeFilter?.step || CMS_DEFAULT_STOCKCAR_RANGE_STEP,
		);

	useEffect(() => {
		open &&
			appTrackingService.viewChangeEvent({
				eventName: `configurator (stock car matching) - show ${locationView === 'UserLocationView' ? 'location filter' : 'car matching'} layer`,
				componentName: 'configurator-stock-car-matching',
			});
	}, [locationView, open]);

	return (
		<Dialog
			classNameContent="inherit"
			classNameDialog={locationView === 'UserLocationView' ? 'location-search' : 'stock-car-dialog'}
			open={open}
			onCloseButtonClick={close}>
			{locationView === 'UserLocationView' ? (
				<UserLocationView
					location={location}
					onGeoDataChange={setGeoData}
					radius={radius}
					onRadiusChange={setRadius}
					onLocationChange={setLocation}
				/>
			) : locationView === 'StockCarView' ? (
				<CarMatchingResultsDialog searchLocation={location} geoData={geoData} radius={radius} />
			) : (
				<div>404</div>
			)}
		</Dialog>
	);
};

export const StockCarEnabled = () => {
	const {
		config: { stockCarConfig, summaryFooterCta },
	} = useMarketConfig();
	return stockCarConfig.enabled && summaryFooterCta.trackingName !== 'external Stockcar';
};
